import API_BASE_URL from '../config/api';

const DEFAULT_BASE_PRICES = {
  floating_left: 7,
  top_banner: 10,
  section: 5
};

const DEFAULT_PRICE_PER_DAY = {
  floating_left: 1.5,
  top_banner: 2,
  section: 1
};

const VALID_MODES = new Set(['combined', 'calendar_only', 'seconds_only', 'engagement_only']);

let pricingConfigCache = null;
let pricingConfigPromise = null;

export const loadPricingConfig = async () => {
  if (Array.isArray(pricingConfigCache) && pricingConfigCache.length > 0) {
    return pricingConfigCache;
  }
  if (pricingConfigPromise) {
    return pricingConfigPromise;
  }
  try {
    pricingConfigPromise = fetch(`${API_BASE_URL}/promotions/public/pricing-config`)
      .then(async (response) => {
        if (response.ok) {
          pricingConfigCache = await response.json();
          return pricingConfigCache;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        pricingConfigPromise = null;
      });
    await pricingConfigPromise;
  } catch (error) {
    console.warn('Impossible de charger la configuration de tarification, utilisation des valeurs par défaut');
  }
  return pricingConfigCache;
};

const durationDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  const s = startDate instanceof Date ? startDate : new Date(startDate);
  const e = endDate instanceof Date ? endDate : new Date(endDate);
  if (e < s) return 1;
  const ms = e.getTime() - s.getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
  return Math.max(1, days);
};

/**
 * Aligné sur afrodineapi/app/services/promotion_pricing.py — prévisualisation côté client.
 */
export function calculatePromotionServiceFee(config, params = {}) {
  const {
    startDate = null,
    endDate = null,
    displayDurationSeconds = null,
    expectedClicks = null,
    expectedViews = null,
    mediaType = 'image',
    isFeatured = false
  } = params;

  const fmt = (config?.display_format || 'section').trim() || 'section';
  const modeRaw = (config?.pricing_mode || 'combined').toLowerCase();
  const mode = VALID_MODES.has(modeRaw) ? modeRaw : 'combined';

  const basePrice =
    config?.base_price != null
      ? Number.parseFloat(config.base_price)
      : DEFAULT_BASE_PRICES[fmt] ?? 0;
  const pricePerDay =
    config?.price_per_day != null
      ? Number.parseFloat(config.price_per_day)
      : DEFAULT_PRICE_PER_DAY[fmt] ?? 0;
  const days = durationDays(startDate, endDate);
  const cal = basePrice + Math.max(0, days - 1) * pricePerDay;

  let sec = 0;
  if (
    displayDurationSeconds != null &&
    config?.price_per_second != null &&
    config?.min_duration_seconds != null
  ) {
    const extra = Math.max(
      0,
      Number(displayDurationSeconds) - Number(config.min_duration_seconds)
    );
    sec = extra * Number.parseFloat(config.price_per_second);
  }

  const clicks = Number(expectedClicks || 0);
  const views = Number(expectedViews || 0);
  const ppc = config?.price_per_click != null ? Number.parseFloat(config.price_per_click) : 0;
  const ppv = config?.price_per_view != null ? Number.parseFloat(config.price_per_view) : 0;
  const eng = clicks * ppc + views * ppv;

  let fee;
  if (mode === 'calendar_only') {
    fee = cal;
  } else if (mode === 'seconds_only') {
    fee = basePrice + sec;
  } else if (mode === 'engagement_only') {
    fee = basePrice + eng;
  } else {
    fee = cal + sec + eng;
  }

  if ((mediaType || '').toLowerCase() === 'video') {
    fee *= 1.25;
  }
  if (isFeatured) {
    fee *= 1.15;
  }

  return Math.round(fee * 100) / 100;
}

const getConfigForFormat = (displayFormat) => {
  if (pricingConfigCache && Array.isArray(pricingConfigCache)) {
    const c = pricingConfigCache.find(
      (x) => x.display_format === displayFormat && x.is_active
    );
    if (c) return c;
  }
  return {
    display_format: displayFormat,
    base_price: DEFAULT_BASE_PRICES[displayFormat] ?? 0,
    price_per_day: DEFAULT_PRICE_PER_DAY[displayFormat] ?? 0,
    pricing_mode: 'combined',
    is_active: true
  };
};

/**
 * @deprecated Préférer calculatePromotionServiceFee avec les paramètres complets.
 */
export const calculatePromotionPrice = (displayFormat, slideDuration, mediaType = 'image') => {
  const config = getConfigForFormat(displayFormat);
  return calculatePromotionServiceFee(config, {
    displayDurationSeconds: slideDuration,
    mediaType
  });
};

export const formatPrice = (price, currency = 'EUR') => {
  if (currency === 'EUR') {
    return `${Number(price).toFixed(2).replace('.', ',')} €`;
  }
  return `$${Number(price).toFixed(2)}`;
};

export const getFormatDescription = (displayFormat) => {
  const descriptions = {
    floating_left: 'Flottant à gauche',
    top_banner: 'Bande en haut de page',
    section: 'Section promotions'
  };
  return descriptions[displayFormat] || displayFormat;
};

export const getDurationOptions = () => {
  return Array.from({ length: 26 }, (_, i) => i + 5);
};

export { getConfigForFormat };
